import { BaseService } from './BaseService';
import { 
  Enterprise, 
  EnterpriseUser, 
  EnterpriseLicenseUsage, 
  EnterpriseReportType, 
  EnterpriseExample, 
  EnterpriseInvitation, 
  EnterpriseAccessLog,
  CreateEnterpriseReportTypeDTO,
  UpdateEnterpriseReportTypeDTO,
  CreateEnterpriseExampleDTO,
  UpdateEnterpriseExampleDTO
} from '../models';

export class EnterpriseAdminService extends BaseService {
  constructor() {
    super();
  }

  // ===== User Management =====
  async inviteUser(enterpriseId, email, isAdmin = false) {
    const response = await this.post(`/enterprises/${enterpriseId}/users/invite`, { email, isAdmin });
    return new EnterpriseInvitation(response.data || response);
  }

  async removeUser(enterpriseId, userId, removedBy) {
    await this.put(`/enterprises/${enterpriseId}/users/${userId}/remove`, { removedBy });
  }

  async getEnterpriseUsers(enterpriseId) {
    const response = await this.get(`/enterprises/${enterpriseId}/users`);
    const users = response.data?.users || response.users || [];
    return users.map(u => new EnterpriseUser(u));
  }

  async getLicenseUsage(enterpriseId) {
    const response = await this.get(`/enterprises/${enterpriseId}/license-usage`);
    return new EnterpriseLicenseUsage(response.data?.licenseUsage || response.licenseUsage);
  }

  async setUserAdmin(enterpriseId, userId, isAdmin) {
    await this.put(`/enterprises/${enterpriseId}/users/${userId}/admin`, { isAdmin });
  }

  async getEnterpriseAdmins(enterpriseId) {
    const response = await this.get(`/enterprises/${enterpriseId}/admins`);
    const users = response.data?.users || response.users || [];
    return users.map(u => new EnterpriseUser(u));
  }

  // ===== Enterprise Management =====
  async getEnterpriseDetails(enterpriseId) {
    const response = await this.get(`/enterprises/${enterpriseId}/details`);
    return new Enterprise(response.data?.enterprise || response.enterprise);
  }

  async updateEnterpriseDetails(enterpriseId, updates) {
    const response = await this.put(`/enterprises/${enterpriseId}/details`, updates);
    return new Enterprise(response.data?.enterprise || response.enterprise);
  }

  // ===== Access Logs =====
  async getEnterpriseAccessLogs(queryParams) {
    const {
      enterpriseId,
      startDate,
      endDate,
      action,
      userId,
      page = 1,
      limit = 50
    } = queryParams;

    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (action) params.append('action', action);
    if (userId) params.append('userId', userId);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const response = await this.get(`/enterprises/${enterpriseId}/access-logs?${params}`);

    const logs = (response.data?.logs || response.logs || []).map(log => new EnterpriseAccessLog(log));
    const total = response.data?.total || response.total || 0;

    return { logs, total };
  }

  // ===== Report Types Management =====
  async createEnterpriseReportType(enterpriseId, data) {
    const dto = new CreateEnterpriseReportTypeDTO(data);
    const response = await this.post(`/enterprises/${enterpriseId}/report-types`, dto);
    return new EnterpriseReportType(response.data?.reportType || response.reportType || response);
  }

  async getEnterpriseReportTypes(enterpriseId) {
    const response = await this.get(`/enterprises/${enterpriseId}/report-types`);
    const reportTypes = response.data?.reportTypes || response.reportTypes || response || [];
    return reportTypes.map(rt => new EnterpriseReportType(rt));
  }


  async updateEnterpriseReportType(enterpriseId, reportTypeId, updates) {
    const dto = new UpdateEnterpriseReportTypeDTO(updates);
    const response = await this.put(`/enterprises/${enterpriseId}/report-types/${reportTypeId}`, dto);
    return new EnterpriseReportType(response.data?.reportType || response.reportType || response);
  }

  async deleteEnterpriseReportType(enterpriseId, reportTypeId) {
    await this.delete(`/enterprises/${enterpriseId}/report-types/${reportTypeId}`);
    return true;
  }

  // ===== Examples Management =====
  async createEnterpriseExample(enterpriseId, data) {
    const dto = new CreateEnterpriseExampleDTO(data);
    const response = await this.post(`/enterprises/${enterpriseId}/examples`, dto);
    return new EnterpriseExample(response.data?.example || response.example || response);
  }

  async getEnterpriseExamples(enterpriseId, reportType, limit) {
    const params = new URLSearchParams();
    params.append('reportType', reportType);
    if (limit) params.append('limit', limit.toString());

    const response = await this.get(`/enterprises/${enterpriseId}/examples?${params}`);
    const examples = response.data?.examples || response.examples || response || [];
    return examples.map(ex => new EnterpriseExample(ex));
  }

  async updateEnterpriseExample(enterpriseId, exampleId, updates) {
    const dto = new UpdateEnterpriseExampleDTO(updates);
    const response = await this.put(`/enterprises/${enterpriseId}/examples/${exampleId}`, dto);
    return new EnterpriseExample(response.data?.example || response.example || response);
  }

  async deleteEnterpriseExample(enterpriseId, exampleId) {
    await this.delete(`/enterprises/${enterpriseId}/examples/${exampleId}`);
    return true;
  }



  async leaveEnterprise(userId, enterpriseId) {
    await this.delete(`/enterprises/${enterpriseId}/users/${userId}/leave`);
    return true;
  }
}

export default EnterpriseAdminService;